import React, { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  forEachMatchingInfo,
  modelGroupSelectionValue,
  modelSelectionValue,
  productSelectionLabel,
} from './matchingUtils';
import SkuTitleWithTranslate from './components/SkuTitleWithTranslate';

export function buildModelProductTree(filteredSkuTitles, matchingGroups) {
  const filteredSet = new Set(filteredSkuTitles || []);
  const modelGroups = new Map(); // mg -> Map(model -> Set(sku))
  const modelsWithoutGroup = new Map(); // model -> Set(sku)
  const productsInGroups = new Set();
  const modelsInModelGroups = new Set(); // models that belong to at least one Model Series

  forEachMatchingInfo(matchingGroups, (info, title) => {
    if (!filteredSet.has(title) || !info?.groupName) return;
    productsInGroups.add(title);
    const model = info.groupName;
    const mg = (info.modelGroup || '').trim();
    if (mg) {
      modelsInModelGroups.add(model);
      if (!modelGroups.has(mg)) modelGroups.set(mg, new Map());
      const models = modelGroups.get(mg);
      if (!models.has(model)) models.set(model, new Set());
      models.get(model).add(title);
    } else {
      if (!modelsWithoutGroup.has(model)) modelsWithoutGroup.set(model, new Set());
      modelsWithoutGroup.get(model).add(title);
    }
  });

  // Models already under a Model Series must not reappear as standalone models below.
  // Fold any stray SKUs (same model, empty modelGroup on some memberships) into the series entry.
  modelsInModelGroups.forEach((model) => {
    const straySkus = modelsWithoutGroup.get(model);
    if (straySkus) {
      modelGroups.forEach((models) => {
        if (!models.has(model)) return;
        straySkus.forEach((sku) => models.get(model).add(sku));
      });
      modelsWithoutGroup.delete(model);
    }
  });

  const ungroupedSkus = (filteredSkuTitles || []).filter((t) => t && !productsInGroups.has(t));

  const sortMapOfSets = (map) =>
    Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, set]) => [name, Array.from(set).sort((a, b) => a.localeCompare(b))]);

  return {
    modelGroups: Array.from(modelGroups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([mg, models]) => ({
        name: mg,
        models: sortMapOfSets(models).map(([model, skus]) => ({ name: model, skus })),
      })),
    modelsWithoutGroup: sortMapOfSets(modelsWithoutGroup).map(([model, skus]) => ({
      name: model,
      skus,
    })),
    ungroupedSkus: [...ungroupedSkus].sort((a, b) => a.localeCompare(b)),
  };
}

/**
 * Selection UI must be path-scoped: a SKU shared by Model A and Model B must not make B
 * look selected when only A is selected. Checked state uses explicit selection keys
 * (series / model / sku), not global title coverage from other groups.
 */
function selectedSetOf(selected) {
  return new Set(selected || []);
}

/** True when this model node is selected as a model (prefixed, or legacy name that is not a SKU). */
function isModelKeySelected(selectedSet, modelName, skus) {
  if (!modelName) return false;
  if (selectedSet.has(modelSelectionValue(modelName))) return true;
  // Legacy unprefixed model name — only if that name is not itself a child SKU.
  if (selectedSet.has(modelName) && !(skus || []).includes(modelName)) return true;
  return false;
}

function isSkuCheckedInContext(selectedSet, sku, modelName, seriesName, skus) {
  if (selectedSet.has(sku)) return true;
  if (isModelKeySelected(selectedSet, modelName, skus)) return true;
  if (seriesName && selectedSet.has(modelGroupSelectionValue(seriesName))) return true;
  return false;
}

function modelSelectionState(selectedSet, modelName, skus, seriesName) {
  if (isModelKeySelected(selectedSet, modelName, skus)) {
    return { checked: true, indeterminate: false };
  }
  if (seriesName && selectedSet.has(modelGroupSelectionValue(seriesName))) {
    return { checked: true, indeterminate: false };
  }
  if (!skus?.length) return { checked: false, indeterminate: false };
  const hit = skus.filter((sku) => selectedSet.has(sku)).length;
  return {
    checked: hit === skus.length,
    indeterminate: hit > 0 && hit < skus.length,
  };
}

function modelSeriesSelectionState(selectedSet, mgNode) {
  const mgKey = modelGroupSelectionValue(mgNode.name);
  if (selectedSet.has(mgKey)) return { checked: true, indeterminate: false };
  if (!mgNode.models?.length) return { checked: false, indeterminate: false };
  let full = 0;
  let partial = 0;
  mgNode.models.forEach((model) => {
    const st = modelSelectionState(selectedSet, model.name, model.skus, null);
    if (st.checked) full += 1;
    else if (st.indeterminate) partial += 1;
  });
  if (full === mgNode.models.length) return { checked: true, indeterminate: false };
  if (full > 0 || partial > 0) return { checked: false, indeterminate: true };
  return { checked: false, indeterminate: false };
}

/** Remove only the given selection keys (and optional exact SKU keys), keep other groups intact. */
function withoutKeys(selected, keysToRemove) {
  const remove = new Set(keysToRemove);
  return (selected || []).filter((sel) => !remove.has(sel));
}

/**
 * Multi-select tree: Model series → Model → SKU (plus ungrouped SKUs).
 */
export default function ModelProductTreeSelect({
  filteredSkuTitles,
  matchingGroups,
  groupNameToProducts,
  modelGroupToProducts,
  selectedProducts,
  onProductsChange,
  label = 'Model / Product(s)',
  placeholder = 'Search models / products...',
  size = 'small',
  compact = true,
  fullWidth = true,
  minWidth,
  popperMinWidth = 420,
  getSkuCountry,
  getDisplayTitle,
  isTranslationActive,
  isTranslatingTitle,
  onToggleTranslate,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(() => new Set());
  const canTranslate = typeof onToggleTranslate === 'function' && typeof getDisplayTitle === 'function';

  const tree = useMemo(
    () => buildModelProductTree(filteredSkuTitles, matchingGroups),
    [filteredSkuTitles, matchingGroups]
  );

  const selectedSet = useMemo(() => selectedSetOf(selectedProducts), [selectedProducts]);

  const searchLower = search.trim().toLowerCase();
  const matchesSearch = (text) => !searchLower || String(text || '').toLowerCase().includes(searchLower);
  const skuMatchesSearch = (sku) => {
    if (matchesSearch(sku)) return true;
    if (!canTranslate || !getDisplayTitle) return false;
    const country = getSkuCountry?.(sku);
    const display = getDisplayTitle(sku, country);
    return display && display !== sku && matchesSearch(display);
  };

  const filteredTree = useMemo(() => {
    if (!searchLower) return tree;

    const filterModel = (model) => {
      if (matchesSearch(model.name)) return model;
      const skus = model.skus.filter(skuMatchesSearch);
      return skus.length ? { ...model, skus } : null;
    };

    const modelGroups = tree.modelGroups
      .map((mg) => {
        if (matchesSearch(mg.name)) return mg;
        const models = mg.models.map(filterModel).filter(Boolean);
        return models.length ? { ...mg, models } : null;
      })
      .filter(Boolean);

    const modelsWithoutGroup = tree.modelsWithoutGroup.map(filterModel).filter(Boolean);
    const ungroupedSkus = tree.ungroupedSkus.filter(skuMatchesSearch);

    return { modelGroups, modelsWithoutGroup, ungroupedSkus };
  }, [tree, searchLower, canTranslate, getDisplayTitle, getSkuCountry]);

  const open = Boolean(anchorEl);

  const toggleExpanded = (key) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const modelKeysFor = (modelName) =>
    modelName ? [modelSelectionValue(modelName), modelName] : [];

  const toggleModelGroup = (mgNode) => {
    const mgKey = modelGroupSelectionValue(mgNode.name);
    const { checked } = modelSeriesSelectionState(selectedSet, mgNode);
    const nestedKeys = mgNode.models.flatMap((m) => [
      modelSelectionValue(m.name),
      m.name,
      ...m.skus,
    ]);
    // Drop this series key and any nested model/sku keys under it — do NOT touch other series/models
    // that merely share SKU titles.
    let next = withoutKeys(selectedProducts, [mgKey, ...nestedKeys]);
    if (!checked) next = [...next, mgKey];
    onProductsChange(next);
  };

  const toggleModel = (modelName, skus, seriesName = null) => {
    const { checked } = modelSelectionState(selectedSet, modelName, skus, seriesName);
    const seriesKey = seriesName ? modelGroupSelectionValue(seriesName) : null;
    const modelKey = modelSelectionValue(modelName);

    if (checked) {
      // Turning off: if parent series is selected, explode series into sibling models first.
      if (seriesKey && selectedSet.has(seriesKey)) {
        const seriesNode = tree.modelGroups.find((mg) => mg.name === seriesName);
        const siblingModelKeys = (seriesNode?.models || [])
          .filter((m) => m.name !== modelName)
          .map((m) => modelSelectionValue(m.name));
        let next = withoutKeys(selectedProducts, [seriesKey, ...modelKeysFor(modelName), ...(skus || [])]);
        next = [...next, ...siblingModelKeys.filter((n) => !next.includes(n))];
        onProductsChange(next);
        return;
      }
      onProductsChange(withoutKeys(selectedProducts, [...modelKeysFor(modelName), ...(skus || [])]));
      return;
    }

    // Turning on: add model key; remove redundant individual SKUs under this model only.
    let next = withoutKeys(selectedProducts, skus || []);
    if (!next.includes(modelKey)) next = [...next, modelKey];
    onProductsChange(next);
  };

  const toggleSku = (sku, modelName = null, seriesName = null, modelSkus = null) => {
    const skus = modelSkus || [];
    const isOn = isSkuCheckedInContext(selectedSet, sku, modelName, seriesName, skus);
    const seriesKey = seriesName ? modelGroupSelectionValue(seriesName) : null;

    if (isOn) {
      // Uncheck: explode parent model / series into remaining siblings + sibling SKUs.
      if (isModelKeySelected(selectedSet, modelName, skus)) {
        const resolvedSkus =
          (skus.length ? skus : null) ||
          groupNameToProducts?.get?.(modelName) ||
          tree.modelsWithoutGroup.find((m) => m.name === modelName)?.skus ||
          tree.modelGroups.flatMap((mg) => mg.models).find((m) => m.name === modelName)?.skus ||
          [];
        let next = withoutKeys(selectedProducts, [...modelKeysFor(modelName), sku]);
        resolvedSkus.forEach((t) => {
          if (t !== sku && !next.includes(t)) next.push(t);
        });
        onProductsChange(next);
        return;
      }
      if (seriesKey && selectedSet.has(seriesKey)) {
        const seriesNode = tree.modelGroups.find((mg) => mg.name === seriesName);
        let next = withoutKeys(selectedProducts, [seriesKey, sku]);
        (seriesNode?.models || []).forEach((model) => {
          if (model.name === modelName) {
            model.skus.forEach((t) => {
              if (t !== sku && !next.includes(t)) next.push(t);
            });
          } else {
            const siblingKey = modelSelectionValue(model.name);
            if (!next.includes(siblingKey)) next.push(siblingKey);
          }
        });
        onProductsChange(next);
        return;
      }
      onProductsChange(withoutKeys(selectedProducts, [sku]));
      return;
    }

    onProductsChange([...(selectedProducts || []).filter((s) => s !== sku), sku]);
  };

  const displayText = useMemo(() => {
    if (!selectedProducts?.length) return '';
    const labels = selectedProducts.map(productSelectionLabel);
    if (labels.length <= 2) return labels.join(', ');
    return `${labels[0]}, ${labels[1]}, ...`;
  }, [selectedProducts]);

  const renderSkuRow = (sku, indentPx, modelName = null, seriesName = null, modelSkus = null) => (
    <Box
      key={`${seriesName || ''}:${modelName || ''}:${sku}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        pl: indentPx,
        pr: 0.5,
        py: 0.1,
        gap: 0.25,
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      <Checkbox
        size="small"
        checked={isSkuCheckedInContext(selectedSet, sku, modelName, seriesName, modelSkus)}
        onChange={() => toggleSku(sku, modelName, seriesName, modelSkus)}
        sx={{ p: 0.25, transform: 'scale(0.8)', flexShrink: 0 }}
      />
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', fontSize: '0.7rem' }}>
        {canTranslate ? (
          <SkuTitleWithTranslate
            title={sku}
            country={getSkuCountry?.(sku)}
            getDisplayTitle={getDisplayTitle}
            isTranslationActive={isTranslationActive}
            isTranslatingTitle={isTranslatingTitle}
            onToggleTranslate={onToggleTranslate}
            onTitleClick={() => toggleSku(sku, modelName, seriesName, modelSkus)}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{ fontSize: '0.7rem', cursor: 'pointer' }}
            onClick={() => toggleSku(sku, modelName, seriesName, modelSkus)}
          >
            {sku}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderModelRow = (model, indent = 1, keyPrefix = 'model', seriesName = null) => {
    const modelKey = `${keyPrefix}:${model.name}`;
    const expanded = searchLower ? true : expandedNodes.has(modelKey);
    const { checked, indeterminate } = modelSelectionState(
      selectedSet,
      model.name,
      model.skus,
      seriesName
    );
    return (
      <Box key={modelKey}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: indent,
            pr: 0.5,
            py: 0.15,
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <IconButton size="small" onClick={() => toggleExpanded(modelKey)} sx={{ p: 0.25 }}>
            {expanded ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}
          </IconButton>
          <Checkbox
            size="small"
            checked={checked}
            indeterminate={indeterminate}
            onChange={() => toggleModel(model.name, model.skus, seriesName)}
            sx={{ p: 0.25, transform: 'scale(0.85)' }}
          />
          <Typography
            variant="body2"
            sx={{ fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}
            onClick={() => toggleModel(model.name, model.skus, seriesName)}
          >
            {model.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
            ({model.skus.length})
          </Typography>
        </Box>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {model.skus.map((sku) => renderSkuRow(sku, indent + 3.5, model.name, seriesName, model.skus))}
        </Collapse>
      </Box>
    );
  };

  return (
    <Box sx={{ width: fullWidth ? '100%' : undefined, minWidth, overflow: 'hidden' }}>
      <TextField
        fullWidth={fullWidth}
        size={size}
        label={label}
        value={displayText}
        placeholder={selectedProducts?.length ? '' : placeholder}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        InputProps={{
          readOnly: true,
        }}
        InputLabelProps={
          compact
            ? {
                sx: {
                  fontSize: '0.65rem',
                  transform: 'translate(14px, 8px) scale(1)',
                  '&.MuiInputLabel-shrink': {
                    transform: 'translate(14px, -6px) scale(0.85)',
                  },
                },
              }
            : undefined
        }
        sx={
          compact
            ? {
                '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem', overflow: 'hidden', cursor: 'pointer' },
                '& .MuiInputBase-input': {
                  padding: '4px 8px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                },
              }
            : {
                '& .MuiInputBase-root': { cursor: 'pointer' },
                '& .MuiInputBase-input': { cursor: 'pointer' },
              }
        }
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setSearch('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: Math.max(popperMinWidth, anchorEl?.offsetWidth || 0),
            maxHeight: 360,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiInputBase-root': { fontSize: '0.75rem', height: 32 } }}
          />
        </Box>
        <Box sx={{ overflow: 'auto', flex: 1, py: 0.5 }}>
          {filteredTree.modelGroups.map((mg) => {
            const mgKey = `mg:${mg.name}`;
            const expanded = searchLower ? true : expandedNodes.has(mgKey);
            const { checked, indeterminate } = modelSeriesSelectionState(selectedSet, mg);
            return (
              <Box key={mgKey}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pl: 0.5,
                    pr: 0.5,
                    py: 0.2,
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <IconButton size="small" onClick={() => toggleExpanded(mgKey)} sx={{ p: 0.25 }}>
                    {expanded ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}
                  </IconButton>
                  <Checkbox
                    size="small"
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={() => toggleModelGroup(mg)}
                    sx={{ p: 0.25, transform: 'scale(0.85)' }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
                    onClick={() => toggleModelGroup(mg)}
                  >
                    {mg.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                    ({mg.models.length} models)
                  </Typography>
                </Box>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  {mg.models.map((model) => renderModelRow(model, 2, `mg:${mg.name}:model`, mg.name))}
                </Collapse>
              </Box>
            );
          })}

          {filteredTree.modelsWithoutGroup.map((model) => renderModelRow(model, 0.5))}

          {filteredTree.ungroupedSkus.map((sku) => renderSkuRow(sku, 1.5))}

          {!filteredTree.modelGroups.length &&
            !filteredTree.modelsWithoutGroup.length &&
            !filteredTree.ungroupedSkus.length && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5, fontSize: '0.75rem' }}>
                No models / products match
              </Typography>
            )}
        </Box>
      </Popover>
    </Box>
  );
}

export { productSelectionLabel };
